import type { LicenceResponse, ListResponse } from '@shared/schemas';
import { all, call, getContext, put, takeLatest } from 'redux-saga/effects';

import { pendingCountRequested } from '@/features/list/client-import-alerts';

import type { ListsGateway } from '../infrastructure/lists.gateway';
import {
  deleteRequested,
  duplicateRequested,
  licencesRequested,
  listsRequested,
  renameRequested,
  transferRequested,
} from './lists.actions';
import { listsActions } from './lists.slice';

const DEFAULT_ERROR = 'Failed to load lists.';

/** Best-effort human message for the failure banner (no domain error union needed). */
function toMessage(e: unknown, fallback = DEFAULT_ERROR): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/** WORKER: fetch every list for the admin listing. All I/O via the injected gateway. */
function* fetchListsWorker() {
  const gateway: ListsGateway = yield getContext('listsGateway');
  yield put(listsActions.loadStarted());
  try {
    const rows = (yield call(gateway.fetchLists)) as ListResponse[];
    yield put(listsActions.loadSucceeded(rows));
  } catch (e) {
    yield put(listsActions.loadFailed(toMessage(e)));
  }
}

/**
 * WORKER: the licences the transfer picker offers.
 *
 * A failure lands an empty list rather than an error, exactly as the Edit
 * wizard does: the picker then renders nothing to choose from, which is the
 * honest outcome, and no banner appears over a table that loaded fine.
 */
function* licencesWorker() {
  const gateway: ListsGateway = yield getContext('listsGateway');
  yield put(listsActions.licencesLoadStarted());
  try {
    const rows = (yield call(gateway.fetchLicences)) as LicenceResponse[];
    yield put(listsActions.licencesLoaded(rows));
  } catch {
    yield put(listsActions.licencesLoaded([]));
  }
}

function* renameWorker(action: ReturnType<typeof renameRequested>) {
  const { id, name } = action.payload;
  const gateway: ListsGateway = yield getContext('listsGateway');
  const trimmed = name.trim();

  yield put(listsActions.mutationStarted({ kind: 'rename', name: trimmed }));
  try {
    yield call(gateway.updateList, id, { name: trimmed });
    yield put(listsActions.mutationSucceeded());
    yield put(listsRequested());
  } catch (e) {
    yield put(listsActions.mutationFailed(toMessage(e, 'Failed to rename the list.')));
  }
}

function* transferWorker(action: ReturnType<typeof transferRequested>) {
  const { id, name, licenceId } = action.payload;
  const gateway: ListsGateway = yield getContext('listsGateway');

  yield put(listsActions.mutationStarted({ kind: 'transfer', name }));
  try {
    // `licence_id` alone: the API treats every field as optional, and echoing
    // the fields that did not change would make a rename out of a transfer.
    yield call(gateway.updateList, id, { licence_id: licenceId });
    yield put(listsActions.mutationSucceeded());
    yield put(listsRequested());
  } catch (e) {
    yield put(listsActions.mutationFailed(toMessage(e, 'Failed to transfer the list.')));
  }
}

function* duplicateWorker(action: ReturnType<typeof duplicateRequested>) {
  const { id, name } = action.payload;
  const gateway: ListsGateway = yield getContext('listsGateway');
  const trimmed = name.trim();

  yield put(listsActions.mutationStarted({ kind: 'duplicate', name: trimmed }));
  try {
    yield call(gateway.duplicateList, id, trimmed);
    yield put(listsActions.mutationSucceeded());
    yield put(listsRequested());
  } catch (e) {
    yield put(listsActions.mutationFailed(toMessage(e, 'Failed to duplicate the list.')));
  }
}

function* deleteWorker(action: ReturnType<typeof deleteRequested>) {
  const { id, name } = action.payload;
  const gateway: ListsGateway = yield getContext('listsGateway');

  yield put(listsActions.mutationStarted({ kind: 'delete', name }));
  try {
    yield call(gateway.deleteList, id);
    yield put(listsActions.mutationSucceeded());
    yield put(listsRequested());
    // A deleted client import leaves the Collection badge (#748).
    yield put(pendingCountRequested());
  } catch (e) {
    yield put(listsActions.mutationFailed(toMessage(e, 'Failed to delete the list.')));
  }
}

/** WATCHERS. */
export function* listsSaga() {
  yield all([
    takeLatest(listsRequested.type, fetchListsWorker),
    takeLatest(licencesRequested.type, licencesWorker),
    takeLatest(renameRequested.type, renameWorker),
    takeLatest(transferRequested.type, transferWorker),
    takeLatest(duplicateRequested.type, duplicateWorker),
    takeLatest(deleteRequested.type, deleteWorker),
  ]);
}
